import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { http } from '@/api/http';
import { ApiErrorResponseBody, PaginationSearchParams, RouteParams } from './types';

const apiBaseUrl = import.meta.env.VITE_CONTENT_ORIGIN ?? window.origin;

export const prependApiBaseUrl = (path: string) => new URL(`/api${path}`, http.defaults.baseURL).toString();

export const getFileUrl = (filePath?: string | null) => (filePath ? `${apiBaseUrl}/${filePath}` : '');

export const replacePathParams = <P extends string>(path: P, params: RouteParams<P>) => {
  let url: string = path;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      url = url.replace(`:${key}`, value as string);
    }
  });
  return url as P;
};

export const getPaginationSearchParamsFromUrl = (urlString: string): PaginationSearchParams => {
  const url = new URL(urlString);
  const itemsPerPage = url.searchParams.get('itemsPerPage');
  const pageIndex = url.searchParams.get('pageIndex');

  return {
    itemsPerPage: itemsPerPage ? Number(itemsPerPage) : 10,
    pageIndex: pageIndex ? Number(pageIndex) : 0,
  };
};

export const getWithRootPathGetter =
  <ServiceName extends string>(serviceName: ServiceName) =>
  <Path extends string>(path: Path) =>
    `${serviceName}${path}` as const;

const REQUEST_ID_HEADERS = ['request-id', 'x-amzn-requestid'] as const;

export type UnifiedError = Error & {
  originalMessage: string;
  requestId?: string;
};

const retrieveRequestId = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;
  const headers = error.response?.headers;
  return REQUEST_ID_HEADERS.map((header) => headers?.[header]).find(Boolean);
};

const buildUnifiedErrorMessage = (message: string, requestId?: string) =>
  requestId ? `RequestID: ${requestId}\nMessage: ${message}` : message;

export const retrieveUnifiedError = (error: unknown, defaultMessage = 'Internal Error'): UnifiedError => {
  const apiError = isAxiosError<ApiErrorResponseBody>(error) ? error.response?.data?.error : undefined;
  const requestId = retrieveRequestId(error);
  const originalMessage = apiError?.message ?? (error as Error)?.message ?? defaultMessage;

  return {
    name: apiError?.code ?? (error as Error)?.name ?? 'Error',
    message: buildUnifiedErrorMessage(originalMessage, requestId),
    originalMessage,
    requestId,
  };
};

export const withRequestErrorHandler = async <T extends Promise<unknown>>(cb: () => T) => {
  try {
    return await cb();
  } catch (e) {
    const error = retrieveUnifiedError(e);
    toast.error(error.message);
    return null;
  }
};
